import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useGoogleMaps } from '../../../../hooks/useGoogleMaps.ts';
import styles from './CommerceDeliveryMap.module.scss';

const DEFAULT_CENTER = { lat: -34.603722, lng: -58.381592 };

const FIT_BOUNDS_PADDING_PX = 56;

function fitMapToCircle(map: any, circle: any) {
  if (!map || !circle?.getBounds) return;
  const bounds = circle.getBounds();
  if (!bounds) return;
  try {
    map.fitBounds(bounds, FIT_BOUNDS_PADDING_PX);
  } catch {
    try {
      map.fitBounds(bounds);
    } catch {
      /* ignore */
    }
  }
}

function scheduleFitMapToCircle(map: any, circle: any) {
  requestAnimationFrame(() => {
    fitMapToCircle(map, circle);
    window.setTimeout(() => fitMapToCircle(map, circle), 120);
  });
}

function mapContainerIsReadable(node: HTMLElement): boolean {
  if (!node.isConnected) return false;
  let el: HTMLElement | null = node;
  while (el) {
    const s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden') {
      return false;
    }
    el = el.parentElement;
  }
  const r = node.getBoundingClientRect();
  return r.width >= 8 && r.height >= 8;
}

interface CommerceDeliveryMapProps {
  lat: number | null;
  lng: number | null;
  radiusKm: number;
  onPositionChange: (lat: number, lng: number) => void;
  mapLayoutActive?: boolean;
}

const CommerceDeliveryMap: React.FC<CommerceDeliveryMapProps> = ({
  lat,
  lng,
  radiusKm,
  onPositionChange,
  mapLayoutActive = true,
}) => {
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<any>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  const initGeoRef = useRef({ lat, lng, radiusKm });
  initGeoRef.current = { lat, lng, radiusKm };
  const bootingRef = useRef(false);
  const { isLoaded, loadError } = useGoogleMaps();

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  useLayoutEffect(() => {
    let layoutTimer = 0;
    const retryTimers: number[] = [];
    let dragEndListener: { remove?: () => void } | null = null;
    let clickListener: { remove?: () => void } | null = null;
    let resizeHandler: (() => void) | null = null;

    const destroy = () => {
      window.clearTimeout(layoutTimer);
      retryTimers.forEach((id) => window.clearTimeout(id));
      retryTimers.length = 0;
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        window.visualViewport?.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
      }
      if (dragEndListener && typeof dragEndListener.remove === 'function') dragEndListener.remove();
      if (clickListener && typeof clickListener.remove === 'function') clickListener.remove();
      dragEndListener = null;
      clickListener = null;
      const gm = (window as unknown as { google?: { maps: any } }).google?.maps;
      const marker = markerRef.current;
      const circle = circleRef.current;
      const map = mapRef.current;
      if (marker) marker.setMap(null);
      if (circle) circle.setMap(null);
      if (map && gm?.event) {
        try {
          gm.event.clearInstanceListeners(map);
        } catch {
          /* ignore */
        }
      }
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };

    if (!isLoaded || loadError || !elRef.current) {
      destroy();
      return () => destroy();
    }

    if (!mapLayoutActive) {
      destroy();
      return () => destroy();
    }

    const el = elRef.current;
    const g = (window as unknown as { google?: { maps: any } }).google?.maps;
    if (!g || !el) {
      return () => destroy();
    }

    let cancelled = false;

    const nudgeLayout = (map: any) => {
      try {
        g.event.trigger(map, 'resize');
        const c = map.getCenter();
        if (c) map.setCenter(c);
      } catch {
        /* ignore */
      }
    };

    const tryAttach = () => {
      if (cancelled || mapRef.current || !elRef.current || bootingRef.current) return;
      const node = elRef.current;
      if (!(node instanceof HTMLElement)) return;
      if (!mapContainerIsReadable(node)) return;

      bootingRef.current = true;
      try {
        const { lat: la, lng: ln, radiusKm: rk } = initGeoRef.current;
        const hasCoords = la != null && ln != null && Number.isFinite(la) && Number.isFinite(ln);
        const center = hasCoords ? { lat: Number(la), lng: Number(ln) } : DEFAULT_CENTER;

        const isCoarsePointer =
          typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

        let map: any;
        try {
          map = new g.Map(node, {
            center,
            zoom: 8,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: !isCoarsePointer,
            zoomControl: true,
            zoomControlOptions: {
              position: g.ControlPosition.RIGHT_BOTTOM,
            },
            scrollwheel: true,
            gestureHandling: isCoarsePointer ? 'cooperative' : 'greedy',
            draggable: true,
            clickableIcons: false,
            disableDefaultUI: false,
          });
        } catch {
          return;
        }
        mapRef.current = map;

        const nudge = () => {
          nudgeLayout(map);
          fitMapToCircle(map, circleRef.current);
        };

        resizeHandler = () => {
          if (mapRef.current) nudge();
        };
        window.addEventListener('resize', resizeHandler);
        window.visualViewport?.addEventListener('resize', resizeHandler);
        requestAnimationFrame(() => nudge());
        layoutTimer = window.setTimeout(() => {
          nudge();
        }, 300);

        const marker = new g.Marker({
          position: center,
          map,
          draggable: true,
          zIndex: 999,
        });
        markerRef.current = marker;

        const circle = new g.Circle({
          map,
          center,
          radius: Math.max(1, rk) * 1000,
          fillColor: '#234ba2',
          fillOpacity: 0.12,
          strokeColor: '#234ba2',
          strokeOpacity: 0.55,
          strokeWeight: 1,
          clickable: false,
          zIndex: 1,
        });
        circleRef.current = circle;

        scheduleFitMapToCircle(map, circle);

        dragEndListener = marker.addListener('dragend', () => {
          const p = marker.getPosition?.();
          if (p) {
            const nlat = p.lat();
            const nlng = p.lng();
            onPositionChangeRef.current(nlat, nlng);
            circle.setCenter({ lat: nlat, lng: nlng });
            scheduleFitMapToCircle(map, circle);
          }
        });

        clickListener = map.addListener('click', (e: { latLng?: { lat: () => number; lng: () => number } }) => {
          const ll = e.latLng;
          if (!ll) return;
          const nlat = ll.lat();
          const nlng = ll.lng();
          marker.setPosition({ lat: nlat, lng: nlng });
          circle.setCenter({ lat: nlat, lng: nlng });
          onPositionChangeRef.current(nlat, nlng);
          scheduleFitMapToCircle(map, circle);
        });
      } finally {
        bootingRef.current = false;
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tryAttach();
        [0, 80, 200, 450, 900].forEach((ms) => {
          retryTimers.push(
            window.setTimeout(() => {
              tryAttach();
            }, ms)
          );
        });
      });
    });

    return () => {
      cancelled = true;
      destroy();
    };
  }, [isLoaded, loadError, mapLayoutActive]);

  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    const circle = circleRef.current;
    if (!marker || !circle) return;
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const pos = { lat, lng };
    marker.setPosition(pos);
    circle.setCenter(pos);
    if (map) {
      const g = (window as unknown as { google?: { maps: any } }).google?.maps;
      if (g?.event) {
        try {
          g.event.trigger(map, 'resize');
        } catch {
          /* ignore */
        }
      }
      scheduleFitMapToCircle(map, circle);
    }
  }, [lat, lng]);

  useEffect(() => {
    const map = mapRef.current;
    const circle = circleRef.current;
    if (!circle) return;
    circle.setRadius(Math.max(1, radiusKm) * 1000);
    if (map) {
      scheduleFitMapToCircle(map, circle);
    }
  }, [radiusKm]);

  if (loadError) {
    return <p className={styles.mapFallback}>No se pudo cargar el mapa. Podés seguir: ubicamos tu zona con el texto de cobertura.</p>;
  }

  if (!isLoaded) {
    return <div className={styles.mapLoading}>Cargando mapa…</div>;
  }

  if (!mapLayoutActive) {
    return null;
  }

  return <div ref={elRef} className={styles.mapEl} role="presentation" />;
};

export default CommerceDeliveryMap;
